import { useRef } from 'react';

import { downloadInPlace, isViewableInBrowser } from 'lib/attachmentDisplay';
import { useToastActions } from 'stores/toastStore';

type SignedUrl = { url: string; filename?: string; contentType?: string | null };

// Opens a benefit file through a short-lived URL. The tab is opened inside the click and navigated once
// the URL arrives, so a popup blocker sees the gesture rather than a window.open after an await (the
// useClickAttachment pattern; that hook can't serve these files, which have their own endpoint).
//
// A file the browser can't show — HEIC/HEIF, which the form accepts — would leave that tab blank, so
// none is opened for it: the file downloads in place. The type the file was listed with decides that
// up front, and the type the URL comes back with is checked again in case the listing didn't have one.
//
// One open at a time: a second click while the first is still fetching would open a second tab.
export function useOpenSignedUrl() {
  const { addToast } = useToastActions();
  const inFlight = useRef(false);

  return async (fetchUrl: () => Promise<SignedUrl>, contentType?: string | null) => {
    if (inFlight.current) return;
    inFlight.current = true;

    const viewable = isViewableInBrowser(contentType);
    const tab = viewable ? window.open('', '_blank') : null;
    if (tab) tab.opener = null;

    try {
      const signed = await fetchUrl();
      if (!viewable || !isViewableInBrowser(signed.contentType)) {
        tab?.close();
        downloadInPlace(signed.url);
        addToast({ text: `Downloaded ${signed.filename || 'the file'}` });
        return;
      }

      if (tab) {
        tab.location.replace(signed.url);
      } else if (!window.open(signed.url, '_blank')) {
        throw new Error('Popup blocked');
      }
    } catch {
      tab?.close();
      addToast({ text: 'Could not open the file. Please try again.' });
    } finally {
      inFlight.current = false;
    }
  };
}
